import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, Card, Container, Stack, Typography, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import FormProvider, { RHFCodes, RHFSelect, RHFTextField } from "src/components/hook-form";
import { EVENT_STATUS_CONSTANTS } from "src/config/constants/event-status-constants";
import { useMutationWithAuth, useQueryWithAuth } from "src/graphql/instances";
import { SHOP_APPLY_VOUCHER } from "src/graphql/mutations/vouchers";
import { GET_EVENT_LIST_EVENTMANAGER } from "src/graphql/queries/events";
import { useAuthContext } from "src/hooks/useAuthContext";
import useResponsive from "src/hooks/useResponsive";
import { useLocales } from "src/locales";
import { formatDate } from "src/utils/dateUtils";
import * as Yup from "yup";


export default function VoucherValidationPage() {

    const { translate } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const { currentLang } = useLocales();
    const theme = useTheme();
    const localeString = currentLang.value === 'en' ? 'en-GB' : 'ro-RO';
    const [shopApplyVoucher] = useMutationWithAuth(SHOP_APPLY_VOUCHER);

    const [isLoading, setIsLoading] = useState(false);
    const [appliedVoucher, setAppliedVoucher] = useState(null);

    const {
        loading: loadingEvents,
        data: dataEvents,
    } = useQueryWithAuth(GET_EVENT_LIST_EVENTMANAGER, {
        fetchPolicy: "cache-and-network",
    });

    const events = useMemo(() => dataEvents?.events?.filter(e => e.general.status === EVENT_STATUS_CONSTANTS.ONGOING).map(e => ({ value: e.general.id, label: e.general.name })) ?? [], [dataEvents]);
    const validateSchema = Yup.object().shape({
        code1: Yup.string().required(translate("verifyCode.codeRequired")),
        code2: Yup.string().required(translate("verifyCode.codeRequired")),
        code3: Yup.string().required(translate("verifyCode.codeRequired")),
        code4: Yup.string().required(translate("verifyCode.codeRequired")),
        code5: Yup.string().required(translate("verifyCode.codeRequired")),
        code6: Yup.string().required(translate("verifyCode.codeRequired")),
        eventId: Yup.string()
    });

    const defaultValues = {
        code1: "",
        code2: "",
        code3: "",
        code4: "",
        code5: "",
        code6: "",
        eventId: events[0]?.id ?? ''
    };

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(validateSchema)
    });

    const {
        reset,
        watch,
        control,
        setValue,
        getValues,
        handleSubmit,
    } = methods;


    const onSubmit = async () => {
        if (isLoading) return;
        setIsLoading(true);
        const { code1, code2, code3, code4, code5, code6, eventId } = getValues();
        const code = [code1, code2, code3, code4, code5, code6].join(""); 
        try {
            // console.log(eventId)
            const voucher = await shopApplyVoucher({
                variables: {
                    code: code,
                    eventId: eventId
                }
            })
            setAppliedVoucher(voucher.data.shopValidateApplyVoucher)
            reset();

        } catch (e) {
            enqueueSnackbar(e.message, { variant: "error" });
        } finally {
            setIsLoading(false);
        }

    }



    return (
        <>
            <Helmet>
                <title>{translate("voucherValidation.title")}</title>
            </Helmet>

            <Container maxWidth={"md"} >
                <Typography variant="h3">
                    {translate("voucherValidation.title")}
                </Typography>
                <Typography variant="body1">
                    {translate("voucherValidation.subtitle")}
                </Typography>

                <FormProvider
                    methods={methods}
                    onSubmit={handleSubmit(async () => await onSubmit(false))}
                >
                    <Card sx={{ mt: 6, boxShadow: "0 0 24px rgba(0,0,0,0.15)" }}>
                        <Stack spacing={3} p={4} sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="subtitle1" textAlign={"center"}>
                                {translate("voucherValidation.instructions2")}
                            </Typography>

                            <RHFSelect
                                name="eventId"
                                sx={{
                                    width: 420,
                                    maxWidth: "100%"
                                }}
                            >
                                {events.map((event) => (
                                    <option key={event.value} value={event.value}>
                                        {event.label}
                                    </option>
                                ))}
                            </RHFSelect>

                            <RHFCodes
                                keyName="code"
                                inputs={["code1", "code2", "code3", "code4", "code5", "code6"]}
                                type="string"
                            />

                            <Box display="flex" justifyContent="center">
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    loading={isLoading}
                                    sx={{ width: "auto" }}
                                >
                                    {translate("voucherValidation.validate")}
                                </LoadingButton>
                            </Box>
                        </Stack>

                        {appliedVoucher && //(<Card sx={{ mt: 4 }}>
                            (<Stack spacing={2} pb={4} sx={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="h6" color={theme.palette.success.main}>
                                    {translate('voucherValidation.success')}
                                </Typography>
                                <Typography variant="subtitle1">
                                    {translate('voucherValidation.name')}: {appliedVoucher.title}
                                </Typography>
                                <Typography variant="subtitle1">
                                    {translate('voucherValidation.codeShort')}: {appliedVoucher.code}
                                </Typography>
                                <Typography variant="subtitle1">
                                    {translate('voucherValidation.expiry')}: {formatDate(appliedVoucher.expiryDate, localeString)}
                                </Typography>
                                <Typography variant="subtitle1">
                                    Discount: {appliedVoucher.discount}{appliedVoucher.discountType === "PERCENT" ? "%" : "lei"}
                                </Typography>
                            </Stack>)}

                        {/* </Card>)} */}
                    </Card>
                </FormProvider>

            </Container>
        </>
    )
}